import React from 'react'

import {
  AlignItems,
  Body3,
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import GoustoImage from 'Image'
import Immutable from 'immutable'
import type { Map as ImmutableMap } from 'immutable'

type ProductImages = ImmutableMap<string, ImmutableMap<string, string | number>>

const SummaryImage = withCitrusTheme(GoustoImage, () => ({
  borderRadius: '8px',
  height: '62px',
  objectFit: 'cover',
  width: '62px',
}))

type ProductItemProps = {
  canEditOrder: boolean
  disclaimerKey?: string
  gift?: boolean
  images?: ProductImages
  onRemove?: () => void
  quantity: number
  title: string
}

const getQuantityText = (quantity: number, disclaimerKey?: string, gift?: boolean) => {
  if (gift && quantity === 1) {
    return 'Free Gift!'
  }

  const quantityText = `${quantity} item${quantity === 1 ? '' : 's'}${
    disclaimerKey ? ` ${disclaimerKey}` : ''
  }`

  return gift ? `${quantityText} Free Gift!` : quantityText
}

export const ProductItem = ({
  canEditOrder,
  disclaimerKey,
  gift,
  images,
  onRemove,
  quantity,
  title,
}: ProductItemProps) => {
  const media = images ? images.toList() : Immutable.List()
  const quantityText = getQuantityText(quantity, disclaimerKey, gift)

  return (
    <Box
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={2}
      paddingV={2}
      borderBottomWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      data-testid="order-summary-product-item"
    >
      <Box width="62px" height="62px" flexShrink={0}>
        {media.size > 0 ? (
          <SummaryImage media={media} title={title} contain={false} />
        ) : (
          <Box width="62px" height="62px" bg={Color.NeutralGrey_200} />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.SpaceBetween}
        flexGrow={1}
        minWidth="0"
        height="62px"
      >
        <Text
          size={3}
          color={Color.ColdGrey_800}
          fontFamily={FontFamily.Bold}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Text>
        {quantityText && <Body3>{quantityText}</Body3>}
      </Box>
      {onRemove && canEditOrder ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${title}`}
          style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
        >
          <Icon name="delete" variant={IconVariant.Actionable} />
        </button>
      ) : null}
    </Box>
  )
}
